import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Edit, CheckCircle, Trash2, Clock,
  Calendar, FileText, Download, Truck, PackageCheck,
} from 'lucide-react'
import {
  getDeliveryChallan, approveDeliveryChallan, revertDeliveryChallan,
  dispatchDeliveryChallan, deliverDeliveryChallan, deleteDeliveryChallan,
  downloadDeliveryChallanPdf,
} from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import StatusBadge from '../components/ui/StatusBadge'
import Avatar from '../components/ui/Avatar'
import Tooltip from '../components/ui/Tooltip'
import { useUserMode } from '../contexts/UserModeContext'

/* ── Confirmation Dialog ─────────────────────────────────────────── */
function ConfirmDialog({ open, title, description, confirmLabel, confirmClass, onConfirm, onCancel, children }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 space-y-4 animate-in fade-in">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
        {children}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
          <button type="button" onClick={onConfirm} className={confirmClass || 'btn-primary'}>{confirmLabel || 'Confirm'}</button>
        </div>
      </div>
    </div>
  )
}

/* ── Timeline ────────────────────────────────────────────────────── */
function DCTimeline({ dc }) {
  const events = []

  if (dc.createdAt) {
    events.push({ label: 'Created', date: dc.createdAt, by: dc.createdBy?.name, icon: FileText, color: 'text-gray-400 bg-gray-100' })
  }
  if (dc.approvedAt) {
    events.push({ label: 'Approved', date: dc.approvedAt, by: dc.approvedBy?.name, icon: CheckCircle, color: 'text-blue-500 bg-blue-50' })
  }
  if (dc.dispatchedAt) {
    events.push({ label: 'Dispatched', date: dc.dispatchedAt, icon: Truck, color: 'text-amber-500 bg-amber-50' })
  }
  if (dc.deliveredAt) {
    events.push({ label: 'Delivered', date: dc.deliveredAt, icon: PackageCheck, color: 'text-emerald-500 bg-emerald-50' })
  }

  events.sort((a, b) => new Date(a.date) - new Date(b.date))
  if (events.length === 0) return null

  return (
    <div className="card">
      <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Timeline</h2>
      <div className="relative">
        <div className="absolute left-4 top-2 bottom-2 w-px bg-gray-200" />
        <div className="space-y-4">
          {events.map((ev, i) => {
            const Icon = ev.icon
            return (
              <div key={i} className="relative flex items-start gap-3 pl-1">
                <div className={`relative z-10 flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${ev.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <span className="text-sm font-medium text-gray-900">{ev.label}</span>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                    <span>{formatDate(ev.date)}</span>
                    {ev.by && <><span className="text-gray-300">·</span><span>{ev.by}</span></>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* ── Main Component ──────────────────────────────────────────────── */
export default function DeliveryChallanDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAccountant } = useUserMode()
  const [dc, setDC] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState('')

  const [confirm, setConfirm] = useState({ open: false, title: '', description: '', confirmLabel: '', confirmClass: '', onConfirm: () => {}, extra: null })
  const [dispatchVehicle, setDispatchVehicle] = useState('')
  const [dispatchTransporter, setDispatchTransporter] = useState('')
  const [receivedBy, setReceivedBy] = useState('')
  const dispatchVehicleRef = useRef('')
  const dispatchTransporterRef = useRef('')
  const receivedByRef = useRef('')

  const load = () => {
    setLoading(true)
    getDeliveryChallan(id)
      .then((res) => setDC(res.data))
      .catch(() => navigate('/delivery-challans'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [id])

  const action = async (fn, ...args) => {
    setActionLoading(fn.name || 'loading')
    try {
      const res = await fn(...args)
      if (res?.data?.driveWarning) alert(res.data.driveWarning)
      load()
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed')
    } finally {
      setActionLoading('')
    }
  }

  const openConfirm = (opts) => setConfirm({ open: true, ...opts })
  const closeConfirm = () => {
    setConfirm((c) => ({ ...c, open: false }))
    setDispatchVehicle(''); setDispatchTransporter(''); setReceivedBy('')
    dispatchVehicleRef.current = ''; dispatchTransporterRef.current = ''; receivedByRef.current = ''
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-40 bg-gray-100 rounded-xl" />
        <div className="h-64 bg-gray-100 rounded-xl" />
      </div>
    )
  }

  if (!dc) return null

  const isDraft = dc.status === 'DRAFT'
  const totalTax = Number(dc.cgst || 0) + Number(dc.sgst || 0) + Number(dc.igst || 0)

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Back + Title */}
      <div className="flex items-center gap-3">
        <Link to="/delivery-challans" className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-xl font-semibold text-gray-900">{dc.number || 'DRAFT'}</h1>
      </div>

      {/* Summary Card */}
      <div className="card bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="space-y-3">
            <StatusBadge status={dc.status} size="lg" />
            <div>
              <p className="text-sm text-gray-500">Total Amount</p>
              <p className="text-3xl font-bold text-gray-900 tracking-tight">{formatCurrency(dc.total, dc.currency)}</p>
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Avatar name={dc.customer?.name} size="md" />
              <div className="text-right">
                <p className="font-medium text-gray-900">{dc.billToName || dc.customer?.name || 'No customer'}</p>
                {dc.billToAddress && <p className="text-xs text-gray-500 whitespace-pre-line">{dc.billToAddress}</p>}
                {!dc.billToAddress && dc.customer?.email && <p className="text-xs text-gray-500">{dc.customer.email}</p>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-gray-500">
              <Calendar className="h-3.5 w-3.5" />
              <span>{formatDate(dc.date)}</span>
            </div>
            {(dc.vehicleNumber || dc.transporterName || dc.reasonForTransport) && (
              <div className="text-xs text-gray-500 space-y-0.5">
                {dc.vehicleNumber && <p>Vehicle: <span className="font-mono text-gray-700">{dc.vehicleNumber}</span></p>}
                {dc.transporterName && <p>Transporter: <span className="text-gray-700">{dc.transporterName}</span></p>}
                {dc.reasonForTransport && <p>Reason: <span className="text-gray-700">{dc.reasonForTransport}</span></p>}
              </div>
            )}
            {dc.receivedByName && (
              <p className="text-xs text-gray-500">Received by: <span className="text-gray-700">{dc.receivedByName}</span></p>
            )}
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-2">
        {!isDraft && (
          <button
            onClick={async () => {
              setActionLoading('pdf')
              try {
                const res = await downloadDeliveryChallanPdf(id)
                const url = window.URL.createObjectURL(res.data)
                const a = document.createElement('a')
                a.href = url
                a.download = `${dc.number}.pdf`
                a.click()
                window.URL.revokeObjectURL(url)
              } catch (err) {
                alert(err.response?.data?.error || 'Failed to generate PDF')
              } finally {
                setActionLoading('')
              }
            }}
            disabled={!!actionLoading}
            className="btn-secondary flex items-center gap-1.5"
          >
            <Download className="h-4 w-4" /> {actionLoading === 'pdf' ? 'Generating...' : 'Download PDF'}
          </button>
        )}
        {isDraft && (
          <>
            <Link to={`/delivery-challans/${id}/edit`} className="btn-secondary flex items-center gap-1.5">
              <Edit className="h-4 w-4" /> Edit
            </Link>
            <button
              onClick={() => openConfirm({
                title: 'Approve Delivery Challan',
                description: 'Approving locks this DC for editing. A DC number will be assigned and PDF generated.',
                confirmLabel: 'Approve',
                onConfirm: () => { closeConfirm(); action(approveDeliveryChallan, id) },
              })}
              disabled={!!actionLoading}
              className="btn-primary flex items-center gap-1.5"
            >
              <CheckCircle className="h-4 w-4" /> Approve
            </button>
            <button
              onClick={() => openConfirm({
                title: 'Delete Draft',
                description: 'This will permanently delete this draft delivery challan. This cannot be undone.',
                confirmLabel: 'Delete',
                confirmClass: 'btn-danger',
                onConfirm: () => { closeConfirm(); action(deleteDeliveryChallan, id).then(() => navigate('/delivery-challans')) },
              })}
              disabled={!!actionLoading}
              className="btn-danger flex items-center gap-1.5"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </>
        )}
        {dc.status === 'APPROVED' && (
          <>
            <button
              onClick={() => {
                setDispatchVehicle(dc.vehicleNumber || '')
                setDispatchTransporter(dc.transporterName || '')
                dispatchVehicleRef.current = dc.vehicleNumber || ''
                dispatchTransporterRef.current = dc.transporterName || ''
                openConfirm({
                  title: 'Dispatch Delivery Challan',
                  description: 'Mark this DC as dispatched. You can optionally add transport details.',
                  confirmLabel: 'Dispatch',
                  onConfirm: () => {
                    const data = {}
                    if (dispatchVehicleRef.current) data.vehicleNumber = dispatchVehicleRef.current
                    if (dispatchTransporterRef.current) data.transporterName = dispatchTransporterRef.current
                    closeConfirm()
                    action(dispatchDeliveryChallan, id, data)
                  },
                  extra: 'dispatch',
                })
              }}
              disabled={!!actionLoading}
              className="btn-primary flex items-center gap-1.5"
            >
              <Truck className="h-4 w-4" /> Dispatch
            </button>
            <button
              onClick={() => openConfirm({
                title: 'Revert to Draft',
                description: 'This will revert the DC back to draft. The DC number will be released.',
                confirmLabel: 'Revert to Draft',
                confirmClass: 'btn-secondary',
                onConfirm: async () => { await action(revertDeliveryChallan, id); closeConfirm() },
              })}
              disabled={!!actionLoading}
              className="btn-ghost flex items-center gap-1.5 text-amber-600 hover:text-amber-700"
            >
              <Edit className="h-4 w-4" /> Revert to Draft
            </button>
          </>
        )}
        {dc.status === 'DISPATCHED' && (
          <button
            onClick={() => {
              setReceivedBy('')
              receivedByRef.current = ''
              openConfirm({
                title: 'Mark as Delivered',
                description: 'Confirm that the goods have been delivered.',
                confirmLabel: 'Mark Delivered',
                onConfirm: () => {
                  const data = {}
                  if (receivedByRef.current) data.receivedByName = receivedByRef.current
                  closeConfirm()
                  action(deliverDeliveryChallan, id, data)
                },
                extra: 'deliver',
              })
            }}
            disabled={!!actionLoading}
            className="btn-primary flex items-center gap-1.5"
          >
            <PackageCheck className="h-4 w-4" /> Mark as Delivered
          </button>
        )}
      </div>

      {/* Two Column: Line Items + Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card overflow-hidden">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Line Items</h2>
            <div className="overflow-x-auto -mx-4 sm:-mx-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-2.5 font-medium">Description</th>
                    {isAccountant && <th className="px-4 py-2.5 font-medium">HSN/SAC</th>}
                    <th className="px-4 py-2.5 font-medium text-right">Qty</th>
                    <th className="px-4 py-2.5 font-medium text-right">Rate</th>
                    {isAccountant ? (
                      <>
                        <th className="px-4 py-2.5 font-medium text-right">GST %</th>
                        <th className="px-4 py-2.5 font-medium text-right">CGST</th>
                        <th className="px-4 py-2.5 font-medium text-right">SGST</th>
                        <th className="px-4 py-2.5 font-medium text-right">IGST</th>
                      </>
                    ) : (
                      <th className="px-4 py-2.5 font-medium text-right">Tax</th>
                    )}
                    <th className="px-4 py-2.5 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dc.lineItems?.map((li, i) => {
                    const lineTax = Number(li.cgst || 0) + Number(li.sgst || 0) + Number(li.igst || 0)
                    return (
                      <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-gray-900">{li.description}</div>
                          {li.detailedDescription && (
                            <div className="text-xs text-gray-500 mt-0.5 whitespace-pre-wrap">{li.detailedDescription}</div>
                          )}
                        </td>
                        {isAccountant && <td className="px-4 py-2.5 text-gray-500">{li.hsnSacCode || '—'}</td>}
                        <td className="px-4 py-2.5 text-right text-gray-700">{Number(li.qty)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-700">{formatCurrency(li.rate, dc.currency)}</td>
                        {isAccountant ? (
                          <>
                            <td className="px-4 py-2.5 text-right text-gray-500">{Number(li.gstRate)}%</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(li.cgst || 0, dc.currency)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(li.sgst || 0, dc.currency)}</td>
                            <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(li.igst || 0, dc.currency)}</td>
                          </>
                        ) : (
                          <td className="px-4 py-2.5 text-right text-gray-500">{formatCurrency(lineTax, dc.currency)}</td>
                        )}
                        <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{formatCurrency(li.total, dc.currency)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mt-4 pt-4 border-t border-gray-100">
              <div className="w-72 space-y-2 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>Subtotal</span>
                  <span className="text-gray-900">{formatCurrency(dc.subtotal, dc.currency)}</span>
                </div>
                {isAccountant ? (
                  <>
                    {Number(dc.cgst) > 0 && <div className="flex justify-between text-gray-500"><span>CGST</span><span>{formatCurrency(dc.cgst, dc.currency)}</span></div>}
                    {Number(dc.sgst) > 0 && <div className="flex justify-between text-gray-500"><span>SGST</span><span>{formatCurrency(dc.sgst, dc.currency)}</span></div>}
                    {Number(dc.igst) > 0 && <div className="flex justify-between text-gray-500"><span>IGST</span><span>{formatCurrency(dc.igst, dc.currency)}</span></div>}
                  </>
                ) : (
                  totalTax > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <Tooltip text="Includes CGST, SGST, or IGST as applicable"><span>Tax</span></Tooltip>
                      <span>{formatCurrency(totalTax, dc.currency)}</span>
                    </div>
                  )
                )}
                <div className="flex justify-between font-semibold text-base border-t border-gray-200 pt-2 mt-2">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">{formatCurrency(dc.total, dc.currency)}</span>
                </div>
              </div>
            </div>
          </div>

          {dc.notes && (
            <div className="card">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2">Notes</h2>
              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">{dc.notes}</p>
            </div>
          )}

          {isAccountant && (
            <div className="card bg-gray-50 border-dashed">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Details</h2>
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Currency</dt>
                  <dd className="font-medium text-gray-900">{dc.currency}</dd>
                </div>
                {dc.placeOfSupply && (
                  <div>
                    <dt className="text-gray-500">Place of Supply</dt>
                    <dd className="font-medium text-gray-900">{dc.placeOfSupply}</dd>
                  </div>
                )}
                {dc.createdBy && (
                  <div>
                    <dt className="text-gray-500">Created by</dt>
                    <dd className="font-medium text-gray-900 flex items-center gap-1.5">
                      <Avatar name={dc.createdBy.name} size="sm" />
                      {dc.createdBy.name}
                    </dd>
                  </div>
                )}
                {dc.approvedBy && (
                  <div>
                    <dt className="text-gray-500">Approved by</dt>
                    <dd className="font-medium text-gray-900 flex items-center gap-1.5">
                      <Avatar name={dc.approvedBy.name} size="sm" />
                      {dc.approvedBy.name}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <DCTimeline dc={dc} />
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        description={confirm.description}
        confirmLabel={confirm.confirmLabel}
        confirmClass={confirm.confirmClass}
        onConfirm={confirm.onConfirm}
        onCancel={closeConfirm}
      >
        {confirm.extra === 'dispatch' && (
          <div className="mt-3 space-y-3">
            <div>
              <label className="label">Vehicle Number <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                className="input font-mono"
                value={dispatchVehicle}
                onChange={(e) => { const v = e.target.value.toUpperCase(); setDispatchVehicle(v); dispatchVehicleRef.current = v }}
                placeholder="KA01AB1234"
              />
            </div>
            <div>
              <label className="label">Transporter <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                className="input"
                value={dispatchTransporter}
                onChange={(e) => { setDispatchTransporter(e.target.value); dispatchTransporterRef.current = e.target.value }}
                placeholder="Transporter / courier name"
              />
            </div>
          </div>
        )}
        {confirm.extra === 'deliver' && (
          <div className="mt-3">
            <label className="label">Received By <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              className="input"
              value={receivedBy}
              onChange={(e) => { setReceivedBy(e.target.value); receivedByRef.current = e.target.value }}
              placeholder="Name of person who received the goods"
            />
          </div>
        )}
      </ConfirmDialog>
    </div>
  )
}
